import React from 'react';
import { Box } from '@mui/material';
import { SpotRect } from './useTargetRect';

const SCRIM = 'rgba(11, 18, 32, 0.58)';
const RING = '#2450C6';

type Props = {
  rect: SpotRect | null;
  padding: number;
  radius: number;
  interactive: boolean;
  zIndex: number;
};

export default function TourSpotlight({ rect, padding, radius, interactive, zIndex }: Props) {
  const viewportWidth = typeof window === 'undefined' ? 0 : window.innerWidth;
  const viewportHeight = typeof window === 'undefined' ? 0 : window.innerHeight;
  const clear = rect
    ? {
      top: Math.max(0, rect.top - padding),
      left: Math.max(0, rect.left - padding),
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    }
    : null;

  const scrim = (style: React.CSSProperties) => (
    <Box
      aria-hidden
      sx={{
        position: 'fixed', bgcolor: SCRIM, zIndex,
        transition: 'top 180ms ease-out, left 180ms ease-out, width 180ms ease-out, height 180ms ease-out',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
      style={style}
    />
  );

  if (!clear) {
    return scrim({ top: 0, left: 0, width: '100vw', height: '100vh' });
  }

  return (
    <>
      {scrim({ top: 0, left: 0, width: '100vw', height: Math.max(0, clear.top) })}
      {scrim({ top: clear.top + clear.height, left: 0, width: '100vw', height: Math.max(0, viewportHeight - clear.top - clear.height) })}
      {scrim({ top: clear.top, left: 0, width: Math.max(0, clear.left), height: clear.height })}
      {scrim({ top: clear.top, left: clear.left + clear.width, width: Math.max(0, viewportWidth - clear.left - clear.width), height: clear.height })}
      <Box
        aria-hidden
        sx={{
          position: 'fixed', pointerEvents: 'none', zIndex: zIndex + 1,
          border: `2px solid ${RING}`, boxShadow: '0 0 0 6px rgba(36, 80, 198, 0.18)',
          transition: 'top 180ms ease-out, left 180ms ease-out, width 180ms ease-out, height 180ms ease-out',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
        style={{ top: clear.top, left: clear.left, width: clear.width, height: clear.height, borderRadius: radius }}
      />
      {!interactive && (
        <Box
          aria-hidden
          onMouseDown={event => event.preventDefault()}
          sx={{ position: 'fixed', zIndex: zIndex + 1, cursor: 'default' }}
          style={{ top: clear.top, left: clear.left, width: clear.width, height: clear.height }}
        />
      )}
    </>
  );
}
